import { ApiResult } from '@src/api/apiClient/apiResult';
import { axiosClient, createAbortableConfig } from '@src/api/apiClient/axiosClient';
import { Chat } from '@src/api/shared/community/chatApiClient';

export type Channel = {
  code: string;
  name: string;
  description: string;
  visibility: number;
  allow_file_attachment: number;
  allow_anonymous_post: number;
  email_notification: string;
  in_app_notification: string;
  type: string;
  post_permission: number | null;
  pinned_chat: Chat | null;
  is_access_denied: boolean;
  can_post: boolean;
  emoji: string;
  unread_count?: number;
  mention_count?: string;
  has_unread?: boolean;
  has_mention?: boolean;
  first_unread_chat_id?: number | null;
  first_unread_mentioned_chat_id?: number | null;
  sort_order: number;
};

export type ChannelForm = Omit<
  Channel,
  'visibility' | 'allow_file_attachment' | 'allow_anonymous_post'
> & {
  visibility: number | null;
  allow_file_attachment: number | null;
  allow_anonymous_post: number | null;
};

type ChannelIndexRequest = {
  with_unread?: boolean;
  all?: boolean;
};

type ChannelShowRequest = {
  force_show?: boolean;
};
export type ChannelShowResponse = {
  channel: Channel;
};

type ChannelCreateRequest = {
  channel: ChannelForm;
};
type ChannelCreateResponse = Channel;
export type ChannelCreateErrorResponse = {
  channel: {
    name: string[];
    description: string[];
    visibility: string[];
    allow_file_attachment: string[];
    allow_anonymous_post: string[];
    email_notification: string[];
    in_app_notification: string[];
    type: string[];
    post_permission: string[];
    emoji: string[];
  };
};

type ChannelUpdateRequest = {
  channel: ChannelForm;
};
type ChannelUpdateResponse = Channel;
export type ChannelUpdateErrorResponse = {
  channel: {
    name: string[];
    description: string[];
    visibility: string[];
    allow_file_attachment: string[];
    allow_anonymous_post: string[];
    email_notification: string[];
    in_app_notification: string[];
    type: string[];
    post_permission: string[];
    emoji: string[];
  };
};

type ChannelPinnedChatUpdateRequest = {
  chat_id?: number;
};
type ChannelPinnedChatUpdateResponse = unknown;

type ChannelUpdateOrderRequest = {
  orders: {
    code: string;
    order: number;
  }[];
};

export type ChannelAccessibleChannelIndexResponse = {
  channel: {
    public: string[];
    not_public: string[];
  };
};

type ChannelStatusResponse = unknown;

type ChannelDeleteResponse = unknown;

export const channelApiClient = {
  index: async (
    params: ChannelIndexRequest,
    signal?: AbortSignal,
  ): Promise<ApiResult<Channel[]>> => {
    return await axiosClient.get('/community/channels', {
      params,
      ...createAbortableConfig(signal),
    });
  },
  show: async (
    code: string,
    params?: ChannelShowRequest,
    signal?: AbortSignal,
  ): Promise<ApiResult<ChannelShowResponse>> => {
    return await axiosClient.get(`/community/channels/${code}`, {
      params,
      ...createAbortableConfig(signal),
    });
  },
  create: async (
    params: ChannelCreateRequest,
  ): Promise<ApiResult<ChannelCreateResponse, ChannelCreateErrorResponse>> => {
    return await axiosClient.post(`/community/channels`, params);
  },
  update: async (
    code: string,
    params: ChannelUpdateRequest,
  ): Promise<ApiResult<ChannelUpdateResponse, ChannelUpdateErrorResponse>> => {
    return await axiosClient.put(`/community/channels/${code}`, params);
  },
  delete: async (code: string): Promise<ApiResult<ChannelDeleteResponse>> => {
    return await axiosClient.delete(`/community/channels/${code}`);
  },
  updatePinnedChat: async (
    code: string,
    params: ChannelPinnedChatUpdateRequest,
  ): Promise<ApiResult<ChannelPinnedChatUpdateResponse>> => {
    return await axiosClient.put(`/community/channels/${code}/pinned-chat`, params);
  },
  accessibleChannelIndex: async (): Promise<ApiResult<ChannelAccessibleChannelIndexResponse>> => {
    return await axiosClient.get('/community/channels/accessible');
  },
  updateOrder: async (params: ChannelUpdateOrderRequest): Promise<ApiResult> => {
    return await axiosClient.put('/community/channels/order', params);
  },
  status: async (code: string): Promise<ApiResult<ChannelStatusResponse>> => {
    return await axiosClient.get(`/community/channels/${code}/status`);
  },
};
